import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Conversation } from './types/Conversation';
import ConversationsList from './components/ConversationsList';
import { ConversationsResult } from './types/ConversationsResult';
import ApiProvider from '../../../core/api/ApiProvider';
import { InfoMessageService, TranslationService } from '../../../core/services';
import { ResponseApiType } from '../../types/ResponseApiType';
import { NewMessage } from './types/NewMessage';
import { Message } from './types/Message';
import Status from '../../../core/types/Status';
import { useParams } from 'react-router-dom';
import CaseDetails from './components/CaseDetails';
import MessagesList from './components/MessagesList';
import NewMessageForm from './components/NewMessageForm';
import { AppContext } from '../../../AppContext';
import { useQuery } from 'react-query';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';

const Messages = (props: AbortRequestPropsType) => {
  const context = React.useContext(AppContext);
  const { messageId } = useParams() as { messageId: string; tenantUId: string };
  const [messageSizeLimit, setMessageSizeLimit] = useState<number>(0);
  const [isMobileConversationDetailsEnabled, setIsMobileConversationDetailsEnabled] =
    useState<boolean>(false);
  const [currentConversation, setCurrentConversation] = useState<Conversation | undefined>(
    undefined
  );

  const conversations = useQuery<Status<ConversationsResult>>(
    'conversations',
    async () =>
      await ApiProvider.post<Status<ConversationsResult>>(
        'MessagesModule/GetMessages',
        null,
        props.cancelTokenSource.token
      ),
    {
      enabled: !context.isHideMypageCases,
      refetchOnWindowFocus: false,
      onSuccess: (result) => {
        if (result.data.conversations) {
          setMessageSizeLimit(result.data.messageSizeLimit);
          if (result.data.conversations.length) {
            const selectedConversationId = messageId;
            let selectedIndex = 0;
            if (selectedConversationId !== '') {
              const index = result.data.conversations.findIndex(
                (x) => x.messageId === selectedConversationId
              );
              if (index >= 0) {
                selectedIndex = index;
              }
            }
            if (!result.data.conversations[selectedIndex].notes.at(-1)!.readByDebtor) {
              notifyCaseHandlerMsgRead(
                result.data.conversations[selectedIndex].messageId,
                result.data.conversations[selectedIndex].caseNo
              );
              result.data.conversations[selectedIndex].notes.at(-1)!.readByDebtor = true;
            }

            setCurrentConversation(
              result.data.conversations.length > 0
                ? result.data.conversations[selectedIndex]
                : undefined
            );
          }
        }
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('ErrorSthWentWrong'));
      },
    }
  );

  const notifyCaseHandlerMsgRead = async (msgId: any, caseNumber: string) => {
    await ApiProvider.post<ResponseApiType>('MessagesModule/NotifyCaseHandlerMsgRead', {
      messageId: msgId,
      caseNo: caseNumber,
    });
  };

  const sendMessage = async (message: NewMessage) => {
    if (currentConversation) {
      const request = {
        messageId: currentConversation.messageId,
        caseNo: currentConversation.caseNo,
        noteText: message.content,
        sourceTenantId: currentConversation.sourceTenantId,
      };

      const answerStatus = await ApiProvider.post<ResponseApiType<Message>>(
        'MessagesModule/MessageReply',
        request
      );

      if (answerStatus.success) {
        const notes = currentConversation.notes;
        notes.push(answerStatus.data);
        setCurrentConversation({ ...currentConversation, notes: notes });
      } else {
        InfoMessageService.error('ErrorSthWentWrong');
      }
    }
  };

  const hasConversations =
    conversations.data?.data.conversations && conversations.data.data.conversations.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          minHeight: 50,
        }}
      >
        {isMobileConversationDetailsEnabled && (
          <IconButton
            onClick={() => setIsMobileConversationDetailsEnabled(false)}
            size="small"
            aria-label={TranslationService.translate('Back')}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography
          variant="h6"
          component="h1"
          sx={
            isMobileConversationDetailsEnabled
              ? {
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }
              : { fontWeight: 'bold' }
          }
        >
          {TranslationService.translate('Messages')}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          bgcolor: context.isHideMypageCases ? 'background.paper' : '#f5f5f5',
        }}
      >
        {conversations.isFetching ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : hasConversations ? (
          <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            <Box
              sx={{
                width: { xs: '100%', lg: '42%' },
                flexShrink: 0,
                display: {
                  xs: isMobileConversationDetailsEnabled ? 'none' : 'flex',
                  lg: 'flex',
                },
                flexDirection: 'column',
                borderRight: { lg: 1 },
                borderColor: { lg: 'divider' },
                overflow: 'hidden',
              }}
            >
              <ConversationsList
                conversations={conversations.data!.data.conversations}
                currentConversation={currentConversation}
                onSetCurrentConversation={(element: Conversation) => {
                  setCurrentConversation(element);
                  setIsMobileConversationDetailsEnabled(true);
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                display: {
                  xs: isMobileConversationDetailsEnabled ? 'flex' : 'none',
                  lg: 'flex',
                },
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {currentConversation && (
                <>
                  <CaseDetails currentConversation={currentConversation} />
                  <MessagesList messages={currentConversation.notes} />
                  <NewMessageForm
                    messageSizeLimit={messageSizeLimit}
                    onSendNewMessage={sendMessage}
                  />
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            <Alert severity="info">{TranslationService.translate('NoMessages')}</Alert>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default withAbortRequest(Messages);
